/**
 * Client-side long → wide pivot for PnL reports.
 *
 * The StarGrid serving layer (§6) returns a FLAT GROUP BY result: to obtain the
 * scenario × period axis the FE adds the cube's `scenario_id` + `month` columns to
 * `group_by`, so each returned row is one dim-tuple × scenario × month with a single
 * signed `value`. This module reshapes those long rows into the wide, scenario-grouped
 * model the AG Grid consumes — the same shape the abandoned xPT DuckDB backend used to
 * emit server-side, now computed in the browser (no DuckDB, no cold-S3 re-scan).
 *
 * Deferred (backend not ready — see pnl-reports-feature-design.md §8):
 *   - Statement-line subtotal *layout* (rollup-map JOIN deferred). Statement-line
 *     SIGNING already happens server-side, so signed leaf lines render correctly.
 */
import { PNL_CUBE_MONTH_COLUMN, PNL_CUBE_SCENARIO_COLUMN, PNL_CUBE_VALUE_COLUMN } from '../starGridConstants';
import {
  PnlGridRow,
  PnlPivotCalcColumn,
  PnlPivotColumn,
  PnlPivotColumnGroup,
  PnlPivotModel,
  PnlQueryResponse,
  PnlScenarioEntry,
  PnlVariancePair,
  ReportCell
} from '../starGridTypes';

/** Format a YYYYMM month id to "Jan 24". Mirrors ColumnsPicker.formatMonth. */
export const formatMonthShort = (monthId: number): string => {
  const year = Math.floor(monthId / 100);
  const month = monthId % 100;
  const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const name = names[month - 1] ?? String(month);
  return `${name} ${String(year).slice(-2)}`;
};

/** Stable, collision-free wide-column field id for a (scenario, month) cell. */
export const pivotFieldId = (scenarioId: number, month: number): string => `s${scenarioId}_m${month}`;

/** Coerce a cell that may be string | number | null to a finite number or null. */
const toNumericCell = (v: ReportCell | undefined): number | null => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string' && v.length > 0) {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

/** Coerce a cube key cell (scenario_id / month) to an integer, or null. */
const toIntCell = (v: ReportCell | undefined): number | null => {
  const n = toNumericCell(v);
  return n === null ? null : Math.trunc(n);
};

interface ScenarioLabels {
  /** reporting_scenario_id → display label (from the report's saved scenarios). */
  readonly byId: Map<number, string>;
  /** display label → reporting_scenario_id (for variance-pair resolution). */
  readonly idByLabel: Map<string, number>;
  /** report-order of scenario ids, for stable column-group ordering. */
  readonly order: number[];
}

const buildScenarioLabels = (scenarios: readonly PnlScenarioEntry[]): ScenarioLabels => {
  const byId = new Map<number, string>();
  const idByLabel = new Map<string, number>();
  const order: number[] = [];
  for (const s of scenarios) {
    if (!byId.has(s.reporting_scenario_id)) order.push(s.reporting_scenario_id);
    byId.set(s.reporting_scenario_id, s.label);
    idByLabel.set(s.label, s.reporting_scenario_id);
  }
  return { byId, idByLabel, order };
};

/** Dimension fields = returned dimension columns minus the two cube system columns. */
const extractDimensionFields = (response: PnlQueryResponse): string[] =>
  response.columns
    .filter((c) => c.kind === 'dimension')
    .map((c) => c.name)
    .filter((name) => name !== PNL_CUBE_SCENARIO_COLUMN && name !== PNL_CUBE_MONTH_COLUMN);

/** Stable key identifying one wide row from its ordered dimension values. */
const dimKey = (row: Record<string, ReportCell>, dimensionFields: readonly string[]): string =>
  dimensionFields.map((f) => String(row[f] ?? '')).join('\u0000');

/**
 * Reshape a flat long query response into the wide grid model.
 *
 * `scenarios` (the report's saved scenarios) supply the scenario_id → label mapping
 * and the report-order for column groups. `variancePairs` (also from the report) drive
 * the client-computed variance columns.
 */
export const buildPnlPivotModel = (
  response: PnlQueryResponse,
  scenarios: readonly PnlScenarioEntry[],
  variancePairs: readonly PnlVariancePair[]
): PnlPivotModel => {
  const dimensionFields = extractDimensionFields(response);
  const labels = buildScenarioLabels(scenarios);

  // 1. Collapse long rows into wide rows keyed by dimension tuple; record every
  //    observed (scenario_id, month) pair so we can build column groups from data.
  const wideByKey = new Map<string, PnlGridRow>();
  const monthsByScenario = new Map<number, Set<number>>();

  for (const row of response.rows) {
    const scenarioId = toIntCell(row[PNL_CUBE_SCENARIO_COLUMN]);
    const month = toIntCell(row[PNL_CUBE_MONTH_COLUMN]);
    if (scenarioId === null || month === null) continue;

    const key = dimKey(row, dimensionFields);
    let wide = wideByKey.get(key);
    if (!wide) {
      wide = {};
      for (const f of dimensionFields) wide[f] = row[f] ?? null;
      wideByKey.set(key, wide);
    }
    wide[pivotFieldId(scenarioId, month)] = toNumericCell(row[PNL_CUBE_VALUE_COLUMN]);

    let months = monthsByScenario.get(scenarioId);
    if (!months) {
      months = new Set<number>();
      monthsByScenario.set(scenarioId, months);
    }
    months.add(month);
  }

  // 2. Column groups: report scenarios first (in saved order), then any extra
  //    scenario ids the backend surfaced (e.g. Actuals injected for pre-forecast
  //    months), each with its months ascending.
  const orderedScenarioIds = [
    ...labels.order.filter((id) => monthsByScenario.has(id)),
    ...[...monthsByScenario.keys()].filter((id) => !labels.byId.has(id)).sort((a, b) => a - b)
  ];

  const columnGroups: PnlPivotColumnGroup[] = orderedScenarioIds.map((scenarioId) => {
    const months = [...(monthsByScenario.get(scenarioId) ?? [])].sort((a, b) => a - b);
    const columns: PnlPivotColumn[] = months.map((m) => ({
      field: pivotFieldId(scenarioId, m),
      headerName: formatMonthShort(m)
    }));
    return { group_label: labels.byId.get(scenarioId) ?? `Scenario ${scenarioId}`, columns };
  });

  // 3. Variance columns (client-computed). Base/compare months are paired positionally
  //    (xPT contract). Skip a pair index if either scenario id can't be resolved.
  const calculatedColumns: PnlPivotCalcColumn[] = [];
  for (const pair of variancePairs) {
    const baseId = labels.idByLabel.get(pair.base_scenario);
    const compareId = labels.idByLabel.get(pair.compare_scenario);
    if (baseId === undefined || compareId === undefined) continue;
    const group = `${pair.base_scenario} vs ${pair.compare_scenario}`;
    const count = Math.min(pair.base_months.length, pair.compare_months.length);
    for (let i = 0; i < count; i += 1) {
      const baseMonth = pair.base_months[i];
      const compareMonth = pair.compare_months[i];
      const baseField = pivotFieldId(baseId, baseMonth);
      const compareField = pivotFieldId(compareId, compareMonth);
      calculatedColumns.push({
        colId: `var_${baseId}_${compareId}_${baseMonth}_${compareMonth}`,
        headerName: baseMonth === compareMonth ? formatMonthShort(baseMonth) : `${formatMonthShort(baseMonth)} vs ${formatMonthShort(compareMonth)}`,
        expression: `[${baseField}] - [${compareField}]`,
        group
      });
    }
  }

  return { dimensionFields, columnGroups, calculatedColumns, rows: [...wideByKey.values()] };
};
